import React from 'react';
import { Wholesale } from 'features/graphql';
import { calculateWholesaleColumnWidth } from './priceShowColumnWidths';

interface PriceShowWholesaleHeadersProps {
  wholesales: Wholesale[];
  /** 価格表更新日時（アカウント通知の生値。表示形式への変換は本コンポーネントで行う） */
  pricelistUpdate: string | null;
  zoomLevel: number;
  /** 「安」マーク列を表示するか（全医薬品にわたる判定のため親で算出して渡す） */
  showMinPriceColumn: boolean;
}

// 価格表更新日時をタイムゾーン変換なしでフォーマットする関数
const formatPricelistUpdateTime = (dateTimeString: string): string => {
  try {
    // 様々な形式に対応する正規表現
    // "2025-06-12 10:21:15" または "2025-06-12T10:21:15Z" 等
    const match = dateTimeString.match(/(\d{4})-(\d{2})-(\d{2})[T\s](\d{2}):(\d{2}):(\d{2})/);

    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return `${year}/${month}/${day} ${hour}:${minute}:${second}`;
    }

    // マッチしない場合は元の文字列をそのまま返す
    return dateTimeString;
  } catch (error) {
    // エラーが発生した場合も元の文字列を返す
    return dateTimeString;
  }
};

/**
 * 価格表の卸ごとのヘッダー列群を描画する（D4 step5: PriceShowList から renderWholesaleHeaders を分離）。
 * 「安」マーク列の表示判定は、分離前に内部で行っていた同値の判定をやめ、
 * 親算出の showMinPriceColumn（PriceShowWholesaleCells と同じもの）に統一。挙動は分離前と不変。
 */
export const PriceShowWholesaleHeaders: React.FC<PriceShowWholesaleHeadersProps> = ({
  wholesales,
  pricelistUpdate,
  zoomLevel,
  showMinPriceColumn,
}) => {
  if (!wholesales || wholesales.length === 0) {
    return null;
  }

  const pricelistUpdateTime = pricelistUpdate
    ? `更新日時：${formatPricelistUpdateTime(pricelistUpdate)}`
    : '';

  // 左側ヘッダーの各行のおおよその高さの目安 (CSSや実際の表示に合わせて調整が必要)
  // 例えば、テーブルヘッダー全体の高さを H とし、各行が H/5 の高さを持つと仮定
  // または、各行のテキストの line-height や padding から想定する
  const rowHeightEstimate = '1.5em'; // 1行あたりの高さの目安 (調整してください)
  const paddingTopForCell = '0.2rem'; // thのデフォルトのpadding-topに合わせるなど

  return (
    <>
      {wholesales.map((wholesale, index: number) => {
        const columnWidth = calculateWholesaleColumnWidth(wholesale.pricelistWholesaleName || '');

        return (
          <React.Fragment key={wholesale.wholesaleId}>
            {/* 「安」マーク用の細い列 - 条件付きで表示 */}
            {showMinPriceColumn && (
              <th
                rowSpan={5}
                style={{
                  width: `${1.5 * zoomLevel}rem`,
                  minWidth: `${1.5 * zoomLevel}rem`,
                  maxWidth: `${1.5 * zoomLevel}rem`,
                  backgroundColor: 'white',
                  padding: `${paddingTopForCell} 0.1rem`,
                  textAlign: 'center',
                }}
              >
                {/* 空のヘッダー（「安」マーク表示用） */}
              </th>
            )}

            {/* 既存の価格表示列 */}
            <th
              rowSpan={5}
              style={{
                verticalAlign: 'top', // 内容を上揃えで配置開始
                position: 'relative', // position:absolute の基準点
                padding: `${paddingTopForCell} 0.2rem`, // 上下のpaddingを調整（左右を削減）
                backgroundColor: 'white', // スティッキーヘッダー用の背景色
                minWidth: `${columnWidth * zoomLevel}rem`,
                width: `${columnWidth * zoomLevel}rem`,
              }}
            >
              {/* 背景を完全に塗りつぶすためのdiv */}
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'white',
                  zIndex: -1,
                }}
              />

              {index === 0 &&
                pricelistUpdateTime && ( // 最初の卸業者のヘッダーの場合のみ更新日時を表示
                  <div
                    style={{
                      position: 'absolute',
                      top: paddingTopForCell,
                      left: '5px',
                      // 複数列分の幅を確保するために、widthを広く設定
                      width: `${wholesales.length ? wholesales.length * 12 * zoomLevel : 12 * zoomLevel}rem`, // 卸業者数 × (安列2rem + 価格列10rem)
                      fontSize: '0.8em',
                      textAlign: 'left',
                      whiteSpace: 'nowrap', // 改行を防ぐ
                      overflow: 'visible', // コンテンツがはみ出ても表示
                      zIndex: 1, // 他の要素の上に表示
                    }}
                  >
                    {pricelistUpdateTime}
                  </div>
                )}

              {/* 卸業者名 */}
              {/* 「薬価種別」の高さに合わせる。「薬価種別」は通常3行目のヘッダー */}
              <div
                style={{
                  fontWeight: 'bold',
                  position: 'absolute',
                  // 「薬価種別」が上から3行目にあると仮定。
                  // topの位置は (行数 - 1) * 行の高さ + セルのpadding-top
                  // (3行目 - 1) * rowHeightEstimate + paddingTopForCell
                  top: `calc((${rowHeightEstimate} * 2) + (${paddingTopForCell} * 2))`, // 3行目の開始位置に調整
                  left: '0.2rem',
                  right: '0.2rem',
                  textAlign: 'left', // 左寄せに変更
                  whiteSpace: 'pre-wrap', // 改行文字を含むテキストを適切に表示
                  fontSize: '0.9em', // 必要に応じてフォントサイズを調整
                  lineHeight: '1.2', // 行間を詰める
                }}
                title={wholesale.pricelistWholesaleName || ''} // ホバー時に完全な名前を表示
              >
                {wholesale.pricelistWholesaleName || ''}
              </div>
            </th>
          </React.Fragment>
        );
      })}
    </>
  );
};
